import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface PointOfInterestCoordinate {
  latitude: number;
  longitude: number;
}

export interface PointOfInterest {
  id: string;
  name: string;
  description: string;
  status_id: string;
  status: { id: string; name: string };
  location: PointOfInterestCoordinate;
  address: string;
  shared: boolean;
  category_id?: string;
  category?: { id: string; name: string };
  notes?: string;
  company_id: string;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  createdBy: string;
}

export interface CreatePointOfInterestData {
  name: string;
  status_id: string;
  location: PointOfInterestCoordinate;
  address?: string;
  shared: boolean;
  category_id?: string;
  notes?: string;
  createdBy?: string;
}

export interface UpdatePointOfInterestData {
  name?: string;
  status_id?: string;
  location?: PointOfInterestCoordinate;
  address?: string;
  shared?: boolean;
  category_id?: string;
  notes?: string;
  createdBy?: string;
}

interface PointOfInterestStore {
  pointsOfInterest: PointOfInterest[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchPointsOfInterest: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status_id?: string;
    search?: string;
  }) => Promise<void>;
  fetchPointOfInterest: (pointOfInterestId: string) => Promise<{ success: boolean; data?: PointOfInterest; error?: string }>;
  createPointOfInterest: (pointOfInterestData: CreatePointOfInterestData) => Promise<{ success: boolean; data?: PointOfInterest; error?: string }>;
  updatePointOfInterest: (pointOfInterestId: string, pointOfInterestData: UpdatePointOfInterestData) => Promise<{ success: boolean; data?: PointOfInterest; error?: string }>;
  deletePointOfInterest: (pointOfInterestId: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const usePointOfInterestStore = create<PointOfInterestStore>((set, get) => ({
  pointsOfInterest: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchPointsOfInterest: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, error: null });

    try {
      const { data } = await api.get("/pointsInterestList", {
        params: {
          limit: params.limit ?? 20,
          page: params.page ?? 1,
          offset: params.offset ?? 1,
          status_id: params.status_id ?? undefined,
          search: params.search ?? undefined,
          ...params,
        },
      });
      if (getAdminFetchGeneration() !== fetchGen) return;
      set({ pointsOfInterest: data.data || [], meta: data.meta, loading: false });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      console.error("Error fetching points of interest:", err);
      set({ error: getError(err), loading: false });
    }
  },

  fetchPointOfInterest: async (pointOfInterestId: string) => {
    try {
      const { data } = await api.get("/pointsInterestById", {
        data: { pointOfInterestId },
      });
      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error fetching route:", err);
      return { success: false, error: getError(err) };
    }
  },

  createPointOfInterest: async (pointOfInterestData: CreatePointOfInterestData) => {
    try {
      const { data } = await api.post("/pointsInterest", pointOfInterestData);

      const newPointOfInterest = data.data;
      set((state) => ({
        pointsOfInterest: [...state.pointsOfInterest, newPointOfInterest],
      }));

      return { success: true, data: newPointOfInterest };
    } catch (err: any) {
      console.error("Error creating point of interest:", err);
      return { success: false, error: getError(err) };
    }
  },

  updatePointOfInterest: async (pointOfInterestId: string, pointOfInterestData: UpdatePointOfInterestData) => {
    try {
      const { data } = await api.put(`/pointsInterest?id=${pointOfInterestId}`, pointOfInterestData);

      const updatedPointOfInterest = data.data;
      set((state) => ({
        pointsOfInterest: state.pointsOfInterest.map((pointOfInterest) =>
          pointOfInterest.id === pointOfInterestId ? updatedPointOfInterest : pointOfInterest
        ),
      }));

      return { success: true, data: updatedPointOfInterest };
    } catch (err: any) {
      console.error("Error updating point of interest:", err);
      return { success: false, error: getError(err) };
    }
  },

  deletePointOfInterest: async (pointOfInterestId: string) => {
    try {
      await api.delete(`/pointsInterest?id=${pointOfInterestId}`);

      set((state) => ({
        pointsOfInterest: state.pointsOfInterest.filter((pointOfInterest) => pointOfInterest.id !== pointOfInterestId),
      }));

      return { success: true };
    } catch (err: any) {
      console.error("Error deleting point of interest:", err);
      return { success: false, error: getError(err) };
    }
  },

  clearError: () => set({ error: null }),
}));